<template>
  <div>
    <commonModal
      :show="showEffectivDateWarning"
      :actionButton="true"
      :options="{
        title: 'Sorry!',
        description: `Your initial effective date has passed; your new effective date has been updated to today’s date.`,
        buttonLabel: 'OK'
      }"
      @close="clickedCloseOnModal"
    ></commonModal>
    <commonModal
      :show="showCallToModal"
      :options="{
        title: 'Call to complete your quote',
        description:
          `To speak to a Porsche Auto Insurance customer service concierge about completing your quote, please call us at
          <a href='tel:1-` +
          $phoneNumber +
          `'>` +
          $phoneNumber +
          `</a>`,
        buttonLabel: 'OK'
      }"
      @close="showCallToModal = false"
    ></commonModal>
    <legend>{{ name }}</legend>
    <p class="column is-6-desktop is-6-tablet padding-0" v-if="!disableButton">
      For your convenience, this quote has been sent to the email address you
      provided. You can access it anytime by clicking the link in the email, or
      via the "Retrieve your quote" window on the homepage.
    </p>
    <div class="box main-box" v-if="quoteResponse">
      <div class="button-center-align">
        <button
          class="button-primary button-primary--inline button-primary--black -spacing-static-b quote-page-save-my-quote"
          v-if="!disableButton && isLoggedIn"
          @click="saveMyQuote"
        >
          Save My Quote
          <b-icon
            type="is-light"
            pack="fas"
            icon="save"
            size="is-small"
          ></b-icon>
        </button>
        <button
          class="button-primary button-primary--inline button-primary--black -spacing-static-b quote-page-purchase-now-button"
          @click="nextAction"
        >
          Purchase Now
          <b-icon
            type="is-light"
            pack="fas"
            icon="shopping-cart"
            size="is-small"
          ></b-icon>
        </button>
        <button
          class="button-primary button-primary--inline button-primary--black -spacing-static-b quote-page-call-to-complete"
          @click="openTelNumModal()"
        >
          Call to Complete
          <b-icon
            type="is-light"
            pack="fas"
            icon="phone-volume"
            size="is-small"
          ></b-icon>
        </button>
      </div>
      <div class="column details-box">
        <p class="head">
          Quote Summary -
          <span>{{ policyNumber || '(N/A)' }}</span>
        </p>
        <div class="column item item-border">
          <p class="sub-head">Drivers</p>
          <div
            class="columns is-12"
            v-for="(driver, index) in sortedDrivers"
            v-bind:key="index"
          >
            <div class="column is-6">
              {{
                formatTextToTitleCase(driver.firstName + ' ' + driver.lastName)
              }}
            </div>
            <div class="column is-6">{{ getDobValue(driver.dob) }}</div>
          </div>
        </div>
        <div class="column item item-border">
          <p class="sub-head">Policy Coverage</p>
          <div class="columns is-12 item-mobile-border">
            <div class="column is-6">Bodily Injury and Property Damage:</div>
            <div class="column is-6">
              {{
                getFieldValues('Bodily Injury', 0) &&
                  getFieldValues('Bodily Injury', 0).displayLimit
              }}
              /
              {{
                getFieldValues('Property Damage', 0) &&
                  getFieldValues('Property Damage', 0).displayLimit
              }}
            </div>
          </div>
          <div class="columns is-12 item-mobile-border">
            <div class="column is-6">
              Underinsured/Uninsured Motorist Bodily Injury:
            </div>
            <div v-if="getFieldValues('UM/UIMBI', 0)" class="column is-6">
              {{ getFieldValues('UM/UIMBI', 0).displayLimit }}
            </div>
            <div class="column is-6" v-else>Not Applicable</div>
          </div>
          <div
            class="columns is-12 item-mobile-border"
            v-if="policyState !== 'GA'"
          >
            <div class="column is-6">
              Underinsured/Uninsured Property Damage:
            </div>
            <div class="column is-6">
              {{
                getFieldValues('Uninsured Motorist Property Damage', 0) &&
                  getFieldValues('Uninsured Motorist Property Damage', 0)
                    .displayLimit
              }}
            </div>
          </div>
          <div class="columns is-12" v-if="policyState !== 'OR'">
            <div class="column is-6">Medical Payments:</div>
            <div class="column is-6">
              {{
                getFieldValues('Medical Payments', 0) &&
                  getFieldValues('Medical Payments', 0).displayLimit
              }}
            </div>
          </div>
          <div class="columns is-12" v-else>
            <div class="column is-6">Personal Injury Protection:</div>
            <div class="column is-6">
              {{
                getFieldValues('Personal Injury Protection', 0) &&
                  getFieldValues('Personal Injury Protection', 0).displayLimit
              }}
            </div>
          </div>
        </div>
        <div
          v-for="(vehicle, index) in policyVehicles"
          :key="vehicle.carNumber"
          :class="{
            'item-border': index < policyVehicles.length - 1
          }"
        >
          <div class="column item">
            <p class="sub-head">Vehicle</p>
            <div class="columns is-12 item-mobile-border">
              <div class="column is-6">Model:</div>
              <div class="column is-6">
                {{
                  formatTextToTitleCase(vehicle.year) +
                    ' ' +
                    formatTextToTitleCase(vehicle.make) +
                    ' ' +
                    formatTextCapitalCase(vehicle.model) +
                    ' ' +
                    formatStyleToProperCase(vehicle.bodyStyle)
                }}
              </div>
            </div>
            <div class="columns is-12 item-mobile-border">
              <div class="column is-6">VIN:</div>
              <div class="column is-6">{{ vehicle.vin }}</div>
            </div>
            <!--            <div class="columns is-12 item-mobile-border">-->
            <!--              <div class="column is-6">Garaging Address:</div>-->
            <!--              <div class="column is-6">-->
            <!--                {{ formatTextToTitleCase(quoteResponse.addressInfo.streetName) }}-->
            <!--                <br />-->
            <!--                {{-->
            <!--                  formatTextToTitleCase(getAddressInfo.city + ', ') +-->
            <!--                    ', ' +-->
            <!--                    ' ' +-->
            <!--                    getAddressInfo.state +-->
            <!--                    ' ' +-->
            <!--                   getAddressInfo.zip-->
            <!--                }}-->
            <!--              </div>-->
            <!--            </div>-->
            <div
              class="columns is-12 item-mobile-border"
              v-if="!quoteRequestIsNull"
            >
              <div class="column is-6">Insured Value:</div>
              <div class="column is-6">${{ getStatedValue(vehicle.vin) }}</div>
            </div>
          </div>
          <div class="column item">
            <p class="sub-head">Vehicle Coverage</p>
            <div class="columns is-12 item-mobile-border">
              <div class="column is-6">Comprehensive Deductible:</div>
              <div
                class="column is-6"
                v-if="getFieldValues('Comprehensive', index)"
              >
                {{
                  getFieldValues('Comprehensive', index) &&
                    getFieldValues('Comprehensive', index).displayLimit
                }}
              </div>
              <div class="column is-6" v-else>Not Applicable</div>
            </div>
            <div class="columns is-12 item-mobile-border">
              <div class="column is-6">Collision Deductible:</div>
              <div
                class="column is-6"
                v-if="getFieldValues('Collision', index)"
              >
                {{
                  toCurrencyFormat(
                    getFieldValues('Collision', index) &&
                      getFieldValues('Collision', index).displayLimit
                  )
                }}
              </div>
              <div class="column is-6" v-else>Not Applicable</div>
            </div>
            <div class="columns is-12 item-mobile-border">
              <div class="column is-6">Rental Coverage:</div>
              <div class="column is-6" v-if="getFieldValues('Rental', index)">
                {{
                  toCurrencyFormat(
                    formatRentalCoverage(
                      (getFieldValues('Rental', index) &&
                        getFieldValues('Rental', index).displayLimit) ||
                        '0'
                    )
                  )
                }}
              </div>
              <div class="column is-6" v-else>Not Applicable</div>
            </div>
            <div
              class="columns is-12 item-mobile-border"
              v-if="policyState === 'GA'"
            >
              <div class="column is-6">
                Underinsured/Uninsured Property Damage:
              </div>
              <div
                class="column is-6"
                v-if="
                  getFieldValues('Uninsured Motorist Property Damage', index)
                "
              >
                {{
                  getFieldValues('Uninsured Motorist Property Damage', index) &&
                    getFieldValues('Uninsured Motorist Property Damage', index)
                      .displayLimit
                }}
              </div>
              <div class="column is-6" v-else>Not Applicable</div>
            </div>
          </div>
          <div class="column item">
            <p class="sub-head">
              Vehicle Premium ({{
                isPerMile(index) ? 'Per Mile' : 'Unlimited'
              }})
            </p>
            <div
              v-if="isPerMile(index)"
              class="columns is-12 item-mobile-border"
            >
              <div class="column is-6">Base Charge:</div>
              <div class="column is-6">
                <span v-if="vehicleBaseRate(index)"
                  >${{ toCurrencyFormat(vehicleBaseRate(index)) }}</span
                >
                <span v-else>Not Applicable</span> per month
              </div>
            </div>
            <div
              v-if="isPerMile(index)"
              class="columns is-12 item-mobile-border"
            >
              <div class="column is-6">Per Mile Charge:</div>
              <div class="column is-6">
                <span v-if="vehiclePerMileRate(index)"
                  >${{ toCurrencyFormat(vehiclePerMileRate(index)) }}</span
                >
                <span v-else>Not Applicable</span> per mile
              </div>
            </div>
            <div class="columns is-12">
              <div v-if="isPerMile(index)" class="column is-6">
                Estimated monthly premium:
              </div>
              <div v-else class="column is-6">Monthly premium:</div>
              <div class="column is-6">
                <span v-if="monthlyCostForVehicle(index)"
                  >${{ toCurrencyFormat(monthlyCostForVehicle(index)) }}</span
                >
                <span v-else>(N/A)</span>
                (plus applicable fees)
                <i class="icon icon--info tool-tip">
                  <div class="content">
                    <p>{{ monthlyFeeDisclosure }}</p>
                    <i></i>
                  </div>
                </i>
              </div>
            </div>
            <p
              v-if="isPerMile(index) && vehicleEstMonthlyMiles(index) !== null"
            >
              (Based on your estimated driving of
              {{ vehicleEstMonthlyMiles(index) }} miles per month)
            </p>
          </div>
        </div>
        <div class="column item">
          <p class="sub-head">Total Policy Premium</p>
          <div class="columns is-12 item-mobile-border">
            <div class="column is-6">
              Estimated Monthly Payment (includes fees):
              <i class="icon icon--info tool-tip">
                <div class="content">
                  <p>{{ monthlyFeeDisclosure }}</p>
                  <i></i>
                </div>
              </i>
            </div>
            <div class="column is-6">
              <span v-if="estimatedMonthlyPayment"
                >${{ toCurrencyFormat(estimatedMonthlyPayment) }}</span
              >
              <span v-else>(N/A)</span>
            </div>
          </div>
          <div class="columns is-12">
            <div class="column is-6">
              Estimated Six-Month Payment (includes fees):
              <i class="icon icon--info tool-tip">
                <div class="content">
                  <p>{{ paidInFullFeeDisclosure }}</p>
                  <i></i>
                </div>
              </i>
            </div>
            <div class="column is-6">
              <span v-if="estimatedSixMonthPayment"
                >${{ toCurrencyFormat(estimatedSixMonthPayment) }}</span
              >
              <span v-else>(N/A)</span>
            </div>
          </div>
        </div>
      </div>
      <h3 class="proceed-options-title">How would you like to proceed?</h3>
      <div class="button-center-align">
        <button
          v-if="!disableButton && !quoteRequestIsNull"
          @click="$emit('goBack')"
          type="button"
          class="button-primary button-primary--black -spacing-static-b back-button quote-b-button"
        >
          <span class="button-primary__icon icon icon--arrow-right-hair">
            <span class="loader-base button-primary__loader"></span>
          </span>

          <span class="button-primary__label">Back</span>
        </button>
        <button
          class="button-primary button-primary--inline button-primary--black  -spacing-static-b quote-page-save-my-quote"
          v-if="!disableButton && isLoggedIn"
          @click="saveMyQuote"
        >
          Save My Quote
          <b-icon
            type="is-light"
            pack="fas"
            icon="save"
            size="is-small"
          ></b-icon>
        </button>
        <button
          class="button-primary button-primary--inline button-primary--black  -spacing-static-b quote-button quote-page-purchase-now-button"
          @click="nextAction"
          type="button"
        >
          Purchase Now
          <b-icon
            type="is-light"
            pack="fas"
            icon="shopping-cart"
            size="is-small"
          ></b-icon>
        </button>
        <button
          class="button-primary button-primary--inline button-primary--black  -spacing-static-b quote-page-call-to-complete"
          @click="openTelNumModal()"
        >
          Call to Complete
          <b-icon
            type="is-light"
            pack="fas"
            icon="phone-volume"
            size="is-small"
            class="phone-volume-icon"
          ></b-icon>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import commonModal from '../shared/commonModal';
import moment from 'moment';
import * as _ from 'lodash';
import {
  monthlyFees as monthlyFeeTotal,
  monthlyFeeTooltip,
  paidInFullFeeTooltip
} from '@/utils/fees.js';
import { paidInFullDownPayment } from '@/utils/downPayment';
export default {
  props: {
    wizardData: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    name: 'Your Quote',
    vehicle: {
      id: 1,
      model: 'Panamera 4 E-Hybrid Executive',
      year: 2018,
      vin: 'WP0AA2A74JL110963',
      image: 'panamera.jpg'
    },
    form: {
      times: null
    },
    radios: {
      times: [
        {
          name: '8-10',
          label: '8am - 10am'
        },
        {
          name: '10-12',
          label: '10am - 12pm'
        },
        {
          name: '12-2',
          label: '12pm - 2pm'
        },
        {
          name: '2-4',
          label: '2pm - 4pm'
        },
        {
          name: '4-6',
          label: '4pm - 6pm'
        }
      ]
    },
    disableButton: false,
    showCallToModal: false,
    showEffectivDateWarning: false,
    sortedArray: []
  }),
  computed: {
    ...mapGetters([
      'quoteResponse',
      'quoteRequest',
      'isLoggedIn',
      'user',
      'getMonthlyCostForIndividualVehicle'
    ]),
    _() {
      return _;
    },
    policy() {
      return (
        (this.quoteResponse &&
          this.quoteResponse.policies &&
          this.quoteResponse.policies[0]) ||
        null
      );
    },
    policyNumber() {
      return (this.policy && this.policy.policyNumber) || '';
    },
    policyVehicles() {
      return (this.policy && this.policy.vehicleInfo) || [];
    },
    requestVehicles() {
      return (
        (this.policy &&
          this.policy.quoteRequest &&
          this.policy.quoteRequest.vehicles) ||
        []
      );
    },
    policyState() {
      const addressInfo = (this.policy && this.policy.addressInfo) || [];
      return (addressInfo[0] && addressInfo[0].state) || '';
    },
    ratedState() {
      const quoteRequest = (this.policy && this.policy.quoteRequest) || null;
      return (
        (quoteRequest && quoteRequest.ratedState) ||
        (this.policy && this.policy.ratedState) ||
        this.policyState
      );
    },
    // Every vehicle is on the Unlimited plan, so no mileage is billed and the
    // Mileage Monitoring fee does not apply.
    unlimitedOnly() {
      const vehicles = this.requestVehicles;
      return (
        vehicles.length > 0 &&
        vehicles.every(vehicle => vehicle.billingType === 'capped')
      );
    },
    // Sum of the per-vehicle premiums, before fees.
    totalMonthlyPremium() {
      return this.policyVehicles.reduce((total, vehicle, index) => {
        return (
          total +
          (this.toNumber(this.getMonthlyCostForIndividualVehicle(index)) || 0)
        );
      }, 0);
    },
    findBilling() {
      return (this.policy && this.policy.findBilling) || null;
    },
    // The rated figure from findBilling wins when the API returns one; premium
    // plus fees from src/utils/fees.js is the fallback. Same precedence as
    // QuotePayment and the coverage screen.
    estimatedMonthlyPayment() {
      const fromBilling =
        this.findBilling &&
        this.formatAmount(this.findBilling.estimatedMonthlyPayment, 2);
      if (fromBilling) {
        return fromBilling;
      }
      if (!this.policyVehicles.length) {
        return null;
      }
      return this.formatAmount(
        this.totalMonthlyPremium +
          monthlyFeeTotal(this.ratedState, this.unlimitedOnly),
        2
      );
    },
    // The paid-in-full single payment, from the chain shared with the coverage
    // and payment screens.
    estimatedSixMonthPayment() {
      const downPayment = paidInFullDownPayment(this.policy, this.ratedState);
      return downPayment === null ? null : downPayment.toFixed(2);
    },
    monthlyFeeDisclosure() {
      return monthlyFeeTooltip(this.ratedState, this.unlimitedOnly);
    },
    paidInFullFeeDisclosure() {
      return paidInFullFeeTooltip(this.ratedState);
    },
    quoteRequestIsNull() {
      if (
        (this.policy &&
          this.policy.quoteRequest &&
          !_.isEmpty(this.policy.quoteRequest)) ||
        this.quoteRequest
      ) {
        return false;
      }
      return true;
    },
    sortedDrivers() {
      return _.orderBy(
        (this.policy && this.policy.driverInfo) || [],
        'driverNumber'
      );
    }
  },
  components: {
    commonModal
  },
  methods: {
    ...mapActions(['requestQuote', 'saveQuote']),
    ...mapMutations(['toggleLoader']),
    getAddressInfo() {
      const addressInfo =
        (this.policy &&
          this.policy.addressInfo &&
          this.policy.addressInfo[0]) ||
        [];
      const garaging = addressInfo.filter(add => {
        return add.addressType === 'garaging';
      });

      return garaging || addressInfo[0];
    },
    submit() {
      let firstErrorField;

      return new Promise((resolve, reject) => {
        this.$validator.validateAll().then(result => {
          if (result) {
            resolve({
              signature: this.form.signature,
              date: this.form.date
            });
          } else {
            firstErrorField = this.$el.querySelector(
              '[name="' + this.$validator.errors.items[0].field + '"]'
            );
            firstErrorField.focus();

            reject('invalid fields');
          }
        });
      });
    },
    getFieldValues(field, index) {
      const vehicle = this.policyVehicles[index];
      const coverageInfo = (vehicle && vehicle.coverageInfo) || [];
      if (coverageInfo.length) {
        var vehicleField = coverageInfo.filter(item => {
          return item.coverageDescription === field;
        });
        if (field === 'UM/UIMBI' && this.policyState === 'GA') {
          vehicleField = coverageInfo.filter(item => {
            return item.coverageDescription === 'UM/UIMBI Limit';
          });
        } else if (field === 'UM/UIMBI' && !vehicleField.length) {
          vehicleField = coverageInfo.filter(item => {
            return (
              item.coverageDescription &&
              item.coverageDescription.includes(
                'Uninsured Motorist Bodily Injury'
              )
            );
          });
        }
        if (vehicleField && vehicleField.length) {
          return vehicleField[0];
        }
      }
    },
    // Parses an API amount, returning null when it is missing or unusable so
    // callers can fall back to a placeholder instead of rendering NaN.
    toNumber(value) {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : null;
    },
    formatAmount(value, digits) {
      const parsed = this.toNumber(value);
      return parsed === null ? null : parsed.toFixed(digits);
    },
    vehiclePremiumInfo(index) {
      const vehicle = this.policyVehicles[index];
      return (vehicle && vehicle.premiumInfo) || null;
    },
    vehicleBaseRate(index) {
      const premiumInfo = this.vehiclePremiumInfo(index);
      return premiumInfo ? this.formatAmount(premiumInfo.baseRate, 2) : null;
    },
    vehiclePerMileRate(index) {
      const premiumInfo = this.vehiclePremiumInfo(index);
      return premiumInfo ? this.formatAmount(premiumInfo.perMileRate, 3) : null;
    },
    vehicleEstMonthlyMiles(index) {
      const premiumInfo = this.vehiclePremiumInfo(index);
      return premiumInfo ? this.toNumber(premiumInfo.estMonthlyMiles) : null;
    },
    monthlyCostForVehicle(index) {
      return this.formatAmount(
        this.getMonthlyCostForIndividualVehicle(index),
        2
      );
    },
    isPerMile(index) {
      const requestVehicle = this.requestVehicles[index];
      if (requestVehicle && requestVehicle.billingType) {
        return requestVehicle.billingType === 'per_mile';
      }
      // No quoteRequest came back with the policy - infer from the rated
      // per-mile charge instead.
      const premiumInfo = this.vehiclePremiumInfo(index);
      return !!(premiumInfo && this.toNumber(premiumInfo.perMileRate));
    },
    openTelNumModal() {
      this.showCallToModal = true;
    },
    getDobValue(value) {
      if (value) {
        return moment(value, 'YYYY-MM-DD').format('MMM D, YYYY');
      }
    },
    formatTextToTitleCase(textString) {
      return _.startCase(_.toLower(textString));
    },

    formatTextCapitalCase(textString) {
      return _.capitalize(textString);
    },

    formatStyleToProperCase(textString) {
      let resultantString = '';
      const containsHyphens = new RegExp('[-]');
      let pipeSplits = textString.split(' | ');
      for (let i = 0; i < pipeSplits.length; i++) {
        const word = pipeSplits[i];
        let wordSplits = word.split(' ');
        for (let j = 0; j < wordSplits.length; j++) {
          const subWord = wordSplits[j];
          if (containsHyphens.test(subWord)) {
            wordSplits[j] =
              subWord.slice(0, 2) +
              subWord.charAt(2).toUpperCase() +
              subWord.slice(3).toLowerCase();
          } else {
            if (subWord.includes('WD')) {
              subWord.toUpperCase();
            } else {
              wordSplits[j] = this.formatTextCapitalCase(subWord);
            }
          }
        }
        if (i < pipeSplits.length - 1) {
          resultantString += wordSplits.join(' ') + ' | ';
        } else {
          resultantString += wordSplits.join(' ');
        }
      }
      return resultantString;
    },

    formatRentalCoverage(textString) {
      let slashBreak = (textString || '').split('/');
      if (slashBreak.length < 2) {
        return textString || '';
      }
      return slashBreak[0] + 'per day /' + slashBreak[1] + ' per incident';
    },
    saveMyQuote() {
      if (!this.isPastEffectiveDate()) {
        this.$emit('saveMyQuote');
      }
    },
    nextAction() {
      if (!this.isPastEffectiveDate()) {
        this.$emit('nextButtonAction');
      }
    },
    createNewQuoteAndRedirect() {
      let quoteRequest =
        this.policy &&
        this.policy.quoteRequest &&
        !_.isEmpty(this.policy.quoteRequest)
          ? _.cloneDeep(this.policy.quoteRequest)
          : null;
      if (quoteRequest) {
        quoteRequest.effectiveDate = moment()
          .startOf('day')
          .format();
        this.toggleLoader({
          loaderText: 'Loading...'
        });
        let that = this;
        this.requestQuote(quoteRequest)
          .then(() => {
            if (that.isLoggedIn) {
              let payload = {
                policyNumbers: [that.policyNumber],
                emailAddress: that.user.emailAddress
              };
              that
                .saveQuote(payload)
                .then(() => {
                  this.toggleLoader();
                  that.$emit('nextButtonAction');
                })
                .catch(() => {
                  this.toggleLoader();
                  this.$snackbar.open({
                    type: 'is-danger',
                    message: `Something went wrong. Please update your Policy Effective Date`,
                    position: 'is-bottom',
                    duration: 70000
                  });
                  that.$emit('goBack');
                });
            } else {
              this.toggleLoader();
              that.$emit('nextButtonAction');
            }
          })
          .catch(err => {
            let error =
              err.error_messages && err.error_messages.length > 1
                ? err.error_messages[0] + ': ' + err.error_messages[1]
                : `The system is unable to complete your request at this time.
                Please call <nobr><a style="text-decoration:underline;color:#fff" href="tel:1-800-400-4128">800-400-4128</a></nobr>
                to speak to a customer service concierge.`;
            this.toggleLoader();
            this.$snackbar.open({
              type: 'is-danger',
              message: error,
              position: 'is-bottom',
              indefinite: true
            });
            this.$emit('goBack');
          });
      } else {
        this.$snackbar.open({
          type: 'is-danger',
          message: `Something went wrong. Please try again later.`,
          position: 'is-bottom',
          duration: 70000
        });
        this.$router.push({ path: '/' });
      }
    },
    isPastEffectiveDate() {
      if (!this.policy || !this.policy.effectiveDate) {
        return false;
      }
      let rawDate = this.policy.effectiveDate.split('T')[0];
      let effectiveDate = moment(rawDate);
      if (effectiveDate.isBefore(moment(), 'day')) {
        this.showEffectivDateWarning = true;
        return true;
      }
      return false;
    },
    clickedCloseOnModal(value) {
      this.showEffectivDateWarning = false;
      if (value === 1) {
        this.createNewQuoteAndRedirect();
      }
    },
    getStatedValue(vin) {
      if (this.requestVehicles.length) {
        let vehicle = this.requestVehicles.filter(vehicleObject => {
          return vehicleObject.vin === vin;
        });
        if (vehicle && vehicle[0] && vehicle[0].statedValue) {
          return this.toCurrencyFormat(vehicle[0].statedValue);
        }
      }
    },
    toCurrencyFormat(value) {
      if (value === null || value === undefined) {
        return '';
      }
      return value.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    }
  }
};
</script>

<style lang="scss" scoped>
.box {
  padding: 1rem;

  @media (min-width: 1000px) {
    margin: 0 2rem;
    padding: 2rem;
  }

  @media (min-width: 1400px) {
    padding: 3rem 4rem;
  }
}

.is-small {
  margin-left: 0.5rem;
}

.phone-volume-icon {
  .fa-phone-volume {
    transform: rotate(-40deg);
  }
}

.padding-0 {
  padding: 0;
}

.main-box {
  background-color: #dad9de;
  margin: 0;
  @extend .padding-0;
}
.details-box {
  background-color: #fff;
  padding: 20px;
  margin: 30px 0px 40px 0px;
  color: #000;
  font-size: 16px;
  .head {
    width: 100%;
    background-color: #f3f3f3;
    border-bottom: 1px solid #dad9de;
    padding: 10px 15px;
    font-size: 14px;
    margin: 0;
  }
  .sub-head {
    font-weight: bold;
    margin-top: 0;
  }
  .item {
    margin: 0px 1rem 0px 1rem;
    padding: 1rem 0px 1rem 0px;
  }
  .item-border {
    border-bottom: 1px solid #dad9de;
    @media (max-width: 768px) {
      border-bottom: 1px solid #403f45;
    }
  }
  .item-mobile-border {
    @media (max-width: 768px) {
      border-bottom: 1px solid #dad9de;
    }
  }
}
button {
  font-size: 16px;
  height: unset;
  color: #fff;
  cursor: pointer;

  @media (min-width: 480px) {
    &.back-button {
      min-width: unset;
    }
  }
  &:hover {
    .icon {
      color: #d5001c !important;
    }
  }
}
@media (max-width: 760px) {
  .quote-wizard .button-primary + .button-primary {
    margin-left: 0;
    margin-top: 1rem;
  }
}

h2 {
  background-color: $concrete;
  border-bottom: 1px solid $bombay;
  color: $black;
  padding: 1rem;
}

h3,
.total,
tfoot {
  font-family: $family-sans-serif-bold;
}

h3 {
  text-transform: none;
  letter-spacing: 0;
  font-size: inherit;
  margin-bottom: 1rem;
}
.proceed-options-title {
  font-size: 24px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: $title-color;
}

.vehicle-info {
  align-items: center;
  display: flex;
  padding-left: 0.75em;
  padding-right: 0.75em;
}

table {
  margin-bottom: 0;
}

thead {
  border-top: 1px solid $bombay;

  th {
    padding-top: 2rem;
  }
}

tbody {
  tr:last-of-type {
    td {
      padding-bottom: 2rem;
    }
  }
}

tfoot {
  border-bottom: 1px solid $bombay;
  border-top: 1px solid $bombay;

  td {
    padding-bottom: 2rem;
    padding-top: 2rem;
  }
}

form {
  @media (min-width: 1000px) {
    margin: 2rem 8rem 0;
  }
}

fieldset {
  min-height: 0;
}
</style>
